/*
 * DESIGN: Forge & Circuit — Neo-Brutalist Dark Tech
 * AI Survival School: Featured band for the sister brand — electric/ember wash
 * and thick top/bottom rules so it reads as a highlight, not just another section.
 * Emphasis is on the community + weekly cadence, not only the classes.
 */
import Reveal from "./Reveal";
import {
  ArrowRight,
  Briefcase,
  GraduationCap,
  Lightbulb,
  Mail,
  MapPin,
  Newspaper,
  Users,
  Video,
} from "lucide-react";

const SCHOOL_URL = "https://www.aisurvivalschool.online/";

/* What lands in members' hands every week */
const weekly = [
  {
    icon: Video,
    title: "Weekly Video",
    description:
      "A short video breakdown every week — what dropped, what it actually means, and what to do about it.",
  },
  {
    icon: Mail,
    title: "Weekly Newsletter",
    description:
      "One email a week. Plain English. No fluff. Straight to the point, straight to your inbox.",
  },
  {
    icon: Newspaper,
    title: "News & Changes",
    description:
      "The AI updates that matter, filtered out of the noise — new models, new features, new risks.",
  },
  {
    icon: Lightbulb,
    title: "Tips & Tricks",
    description:
      "Prompts, workflows, and shortcuts you can put to work in your business the same day.",
  },
];

/* Ways to plug in */
const formats = [
  { icon: MapPin, label: "In-person Tuesdays — Columbus, OH" },
  { icon: Users, label: "Live 12-week online cohorts" },
  { icon: GraduationCap, label: "Self-paced, on-demand course" },
  { icon: Briefcase, label: "On-site workshops for your team" },
];

export default function AiSurvivalSchool() {
  return (
    <section
      id="ai-survival-school"
      className="py-24 md:py-32 relative overflow-hidden"
    >
      {/* Featured-band treatment */}
      <div className="absolute inset-0 bg-gradient-to-br from-electric/10 via-transparent to-ember/10" />
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-electric via-ember to-electric" />
      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-ember via-electric to-ember" />

      <div className="container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* ── Pitch ── */}
          <div>
            <Reveal
              as="span"
              from="none"
              className="font-mono text-xs text-ember tracking-[0.3em] uppercase block mb-4"
            >
              // AI SURVIVAL SCHOOL
            </Reveal>

            <Reveal
              as="h2"
              from="up"
              duration={0.6}
              className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-6"
            >
              Learn AI Before
              <br />
              <span className="text-electric">AI Replaces You</span>
            </Reveal>

            <Reveal
              as="p"
              from="up"
              duration={0.6}
              delay={0.1}
              className="text-steel-light text-lg leading-relaxed mb-5"
            >
              It isn't just a school — it's a community. Every single week we send out
              what's new in AI, what changed, and the tips and tricks that actually move
              the needle, by video and by newsletter.
            </Reveal>

            <Reveal
              as="p"
              from="up"
              duration={0.6}
              delay={0.15}
              className="text-steel-light leading-relaxed mb-8"
            >
              No jargon, no professor talk. Taught by an operator who runs a 16-truck
              towing company and uses this stuff to compete and win.
            </Reveal>

            {/* Ways to plug in */}
            <Reveal from="up" duration={0.5} delay={0.2} className="mb-10">
              <ul className="flex flex-wrap gap-x-6 gap-y-3">
                {formats.map((f) => (
                  <li
                    key={f.label}
                    className="flex items-center gap-2 text-steel-light text-sm"
                  >
                    <f.icon className="w-4 h-4 text-ember shrink-0" />
                    {f.label}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal
              from="up"
              duration={0.5}
              delay={0.25}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a
                href={SCHOOL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-ember text-white font-heading font-bold text-lg border-[3px] border-ember hover:bg-ember/90 transition-all active:translate-y-[1px] group"
              >
                Join the Community
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href={SCHOOL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-[3px] border-electric/40 text-white font-heading font-semibold text-lg hover:border-electric hover:bg-electric/10 transition-all"
              >
                aisurvivalschool.online
              </a>
            </Reveal>
          </div>

          {/* ── What you get every week ── */}
          <div>
            <Reveal
              as="span"
              from="none"
              className="font-mono text-[10px] text-steel tracking-[0.2em] uppercase block mb-5"
            >
              EVERY WEEK, IN YOUR INBOX
            </Reveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {weekly.map((item, i) => (
                <Reveal
                  key={item.title}
                  from="up"
                  duration={0.5}
                  delay={i * 0.1}
                  className="group relative p-6 border-[3px] border-border bg-card hover:border-electric/50 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="mb-4 w-12 h-12 border-[2px] border-electric/30 bg-electric/10 flex items-center justify-center group-hover:bg-electric/20 transition-colors">
                    <item.icon className="w-6 h-6 text-electric" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-white mb-2">
                    {item.title}
                  </h3>
                  <p className="text-steel-light text-sm leading-relaxed">
                    {item.description}
                  </p>
                  <div className="absolute top-0 right-0 w-8 h-8 border-b-[3px] border-l-[3px] border-electric/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
